import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Container, IconButton, Link, Typography } from '@mui/material'
import React from 'react'

import LazyImage from '@/src/components/common/ui/LazyImage'

interface Airline {
    name: string
    href: string
}

interface DesktopTopAirlinesProps {
    title: string
    airlines: Array<{
        airline: Airline
        airlineCode: string
        logoUrl: string
    }>
    isRTL: boolean
    onScrollLeft: () => void
    onScrollRight: () => void
    scrollContainerRef: React.RefObject<HTMLDivElement>
}

const AirlineCard: React.FC<{
    airline: Airline
    logoUrl: string
}> = ({ airline, logoUrl }) => {
    return (
        <Link
            href={airline.href}
            target="_parent"
            sx={{
                flex: { xs: '0 0 90px', md: '0 0 150px' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                scrollSnapAlign: 'start',
                flexShrink: 0
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'common.white',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: '100px', md: '130px' },
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme =>
                            `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)'}`
                    }
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: '50px', md: '70px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 0.75, md: 1 },
                        position: 'relative'
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: '70px', md: '90px' },
                            height: { xs: '28px', md: '36px' },
                            position: 'relative'
                        }}
                    >
                        <LazyImage
                            src={logoUrl}
                            alt={airline.name}
                            sizes="(max-width: 768px) 70px, 90px"
                            objectFit="contain"
                        />
                    </Box>
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '0.6875rem', md: '0.8125rem' },
                        fontWeight: 500,
                        color: 'text.primary',
                        textAlign: 'center',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }}
                >
                    {airline.name}
                </Typography>
            </Box>
        </Link>
    )
}

export const DesktopTopAirlines: React.FC<DesktopTopAirlinesProps> = ({
    title,
    airlines,
    isRTL,
    onScrollLeft,
    onScrollRight,
    scrollContainerRef
}) => {
    return (
        <Box
            sx={{
                py: { xs: 3, md: 4 },
                // bgcolor: 'grey.50'
            }}
        >
            <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
                {/* Mobile Header */}
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        mb: 2
                    }}
                >
                    <Typography
                        component="h2"
                        variant="h2"
                        sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.125rem'
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                {/* Desktop Header */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        position: 'relative'
                    }}
                >
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', mr: 2 }} />
                    <Typography
                        component="h2"
                        variant="h2"
                        sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.5rem',
                            textAlign: 'center'
                        }}
                    >
                        {title}
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', ml: 2 }} />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        gap: 2,
                        direction: isRTL ? 'rtl' : 'ltr'
                    }}
                >
                    {/* Desktop Arrows */}
                    <IconButton
                        onClick={onScrollLeft}
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexShrink: 0,
                            width: 48,
                            height: 48,
                            position: 'absolute',
                            left: -60,
                            zIndex: 20
                        }}
                    >
                        {isRTL ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />}
                    </IconButton>

                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollBehavior: 'smooth',
                            gap: { xs: 1, md: 2 },
                            direction: isRTL ? 'rtl' : 'ltr',
                            pb: 1,
                            mx: { xs: 0, md: 2 },
                            px: { xs: 2, md: 0 },
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        {airlines.map((item, index) => (
                            <AirlineCard key={index} airline={item.airline} logoUrl={item.logoUrl} />
                        ))}
                    </Box>

                    {/* Desktop Arrows */}
                    <IconButton
                        onClick={onScrollRight}
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexShrink: 0,
                            width: 48,
                            height: 48,
                            position: 'absolute',
                            right: -60,
                            zIndex: 20
                        }}
                    >
                        {isRTL ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />}
                    </IconButton>
                </Box>
            </Container>
        </Box>
    )
}

export type { DesktopTopAirlinesProps }
